import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { BookOpen, Users, Calendar, FileText } from 'lucide-react';
import { Spinner } from '../../components/ui/Spinner';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { getTeacherMyClasses, TeacherClass } from '../../modules/roster/api/teacher.client';
import { listRecentSubmissionsGrouped, type SubmissionDTO } from '../../modules/learning/api/assignments';
import { ROUTES, teacherSubmissionReviewPath } from '../../utils/constants';

export const TeacherDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState<TeacherClass[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [classesError, setClassesError] = useState<string | null>(null);
  const [recentSubmissions, setRecentSubmissions] = useState<Record<string, SubmissionDTO[]>>({});
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  useEffect(() => {
    const loadClasses = async () => {
      setLoadingClasses(true);
      try {
        const items = await getTeacherMyClasses();
        setClasses(items);
        setClassesError(null);
      } catch (error) {
        console.error(error);
        setClassesError('Unable to load classes.');
      } finally {
        setLoadingClasses(false);
      }
    };
    loadClasses();
  }, []);

  const loadSubmissions = useCallback(async () => {
    setLoadingSubmissions(true);
    try {
      const res = await listRecentSubmissionsGrouped(30);
      setRecentSubmissions(res.by_grade ?? {});
    } catch {
      setRecentSubmissions({});
    } finally {
      setLoadingSubmissions(false);
    }
  }, []);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const activeCount = classes.filter((c) => c.status === 'active').length;
  const gradeKeys = Object.keys(recentSubmissions).sort();

  return (
    <Layout>
      <PageHeader
        title="Teacher Dashboard"
        subtitle="Welcome back! Here's your overview"
      />
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary-100 dark:bg-primary-900 rounded-lg">
                <BookOpen className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <CardTitle>My Classes</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{loadingClasses ? '—' : activeCount}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Active classes
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-secondary-100 dark:bg-secondary-900 rounded-lg">
                <Users className="w-6 h-6 text-secondary-600 dark:text-secondary-400" />
              </div>
              <CardTitle>Students</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">0</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Total students
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-success-100 dark:bg-success-900 rounded-lg">
                <Calendar className="w-6 h-6 text-success-600 dark:text-success-400" />
              </div>
              <CardTitle>Upcoming</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">0</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Upcoming sessions
            </p>
          </CardContent>
        </Card>
      </div>
      <div className="mt-8">
        <Card>
          <CardHeader>
            <CardTitle>My Classes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingClasses ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : classesError ? (
              <div className="text-sm text-error-600 dark:text-error-400">{classesError}</div>
            ) : classes.length === 0 ? (
              <div className="text-sm text-gray-600 dark:text-gray-400">No classes assigned yet.</div>
            ) : (
              <div className="space-y-4">
                {classes.map((cls, index) => (
                  <div
                    key={index}
                    className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-4 shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {cls.name}
                          {cls.grade != null && cls.grade !== '' ? ` · Grade ${cls.grade}` : ''}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {cls.academic_year_key ?? 'Year unknown'}
                        </p>
                      </div>
                      <Badge variant={cls.status === 'active' ? 'success' : 'gray'}>
                        {cls.status ?? '—'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Assignments (Submissions) */}
      <div className="mt-8">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Assignments</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(ROUTES.TEACHER_ASSIGNMENTS)}
              >
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingSubmissions ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : gradeKeys.length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">No submissions yet.</p>
            ) : (
              <div className="space-y-6">
                {gradeKeys.map((grade) => (
                  <div key={grade}>
                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                      Grade {grade}
                    </h4>
                    <div className="space-y-2">
                      {recentSubmissions[grade].map((sub) => (
                        <div
                          key={sub.submission_id}
                          className="flex items-center justify-between gap-4 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                              <FileText className="w-5 h-5 text-blue-700 dark:text-blue-300" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 dark:text-white truncate">
                                {sub.assignment_title || sub.assignment_id}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                Student: {sub.student_id}
                                {sub.ocr_score != null && (
                                  <span className="ml-2">· OCR: {sub.ocr_score}/100</span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge
                              variant={
                                sub.status === 'published'
                                  ? 'success'
                                  : sub.status === 'submitted'
                                    ? 'warning'
                                    : 'gray'
                              }
                            >
                              {sub.status}
                            </Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                navigate(teacherSubmissionReviewPath(sub.submission_id), {
                                  state: { from: 'dashboard', seedSubmission: sub },
                                })
                              }
                            >
                              Review
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

    </Layout>
  );
};
